/**
 * State 4 of 9 — company not activated (spec 01 §3 and §18, `PRODUCT.md` §4).
 *
 * **Driven by two different things, and they must not be merged.**
 * `API_CONTRACT.md` §4 says so in as many words: "Hai thứ khác nhau, hai code khác
 * nhau, không được gộp."
 *
 *   reason='company'  `GET /auth/me` → the active entry of `companies[]` carries
 *                     `enabled: false`; every business call answers
 *                     `403 WORKFORCE_COMPANY_DISABLED` at step 2 of the 02 §11
 *                     chain. One company inside this instance. The company's own
 *                     administrator fixes it.
 *
 *   reason='service'  `GET /auth/me` → `service_enabled: false`; every business
 *                     call answers `503 WORKFORCE_SERVICE_DISABLED`. The whole
 *                     instance, from `WORKFORCE_PULSE_ENABLED` in the environment.
 *                     The platform operator fixes it.
 *
 * One component with one discriminating prop, two sentences, two codes on screen,
 * and two different people named as the fix. Merging them into one message would
 * send half the users to the wrong person.
 *
 * That `/auth/me` keeps answering in both cases (API_CONTRACT §4) is the only reason
 * this screen can exist at all — otherwise the dashboard would have nothing to
 * render but a wall of 503s.
 */

import type { ReactNode } from 'react';

import { useT } from '../i18n';
import { StatusNote } from './StatusNote';
import { ErrorCodeLine } from './ErrorCodeLine';

export type CompanyDisabledReason = 'company' | 'service';

const ERROR_CODE: Readonly<Record<CompanyDisabledReason, string>> = {
  company: 'WORKFORCE_COMPANY_DISABLED',
  service: 'WORKFORCE_SERVICE_DISABLED',
};

export interface CompanyDisabledProps {
  readonly reason: CompanyDisabledReason;
  /** The company's display name. Omitted when the session has not chosen one yet. */
  readonly companyName?: string | null;
  readonly correlationId?: string | null;
  readonly actions?: ReactNode;
  readonly testId?: string;
}

export function CompanyDisabled({
  reason,
  companyName = null,
  correlationId = null,
  actions,
  testId = 'state-company-disabled',
}: CompanyDisabledProps): ReactNode {
  const t = useT();
  const title =
    reason === 'company'
      ? t('state.companyDisabled.company.title')
      : t('state.companyDisabled.service.title');
  const body =
    reason === 'company'
      ? t('state.companyDisabled.company.body', { company: companyName ?? '—' })
      : t('state.companyDisabled.service.body');
  const contact =
    reason === 'company' ? t('common.contactAdmin') : t('state.companyDisabled.service.contact');

  return (
    <StatusNote severity="medium" title={title} testId={testId} actions={actions}>
      <p>{body}</p>
      <p className="wp-muted">{contact}</p>
      <ErrorCodeLine code={ERROR_CODE[reason]} correlationId={correlationId} />
    </StatusNote>
  );
}
